import { IdentityHeader } from '@core/http-types';
import {
  openAuthBrowserInNativeApp,
  fetchTokenFromNative,
  isNativeMobileHr,
} from '@core/tellent-mobile-contracts';
import {
  AppKind,
  TellentClientAuthErrorCode,
  TellentClientAuthInitData,
  TellentCurrentUser,
  TellentMember,
  TellentUserResponse,
} from '@core/tellent-types';

import {
  authErrorHandlerExhaustiveGuard,
  TellentClientAppPendingError,
  TellentClientAuthError,
  TellentClientBootstrapError,
  TellentClientMemberNotFoundInOrgError,
  TellentClientMemberSeatNotFoundInOrgError,
  TellentClientOrgGuidMismatchError,
  TellentClientOrgGuidUnavailableError,
  TellentClientTokenInvalidError,
  TellentClientTokenUnavailableError,
  TellentClientUserUnavailableError,
} from './errors';

const headers = [
  ['accept', 'application/json'],
  ['x-json-accent', 'pascal'],
  [IdentityHeader.Client, 'TellentClient'],
];

export const TELLENT_AUTH_ORG_GUID_PARAM = 'ts_org_guid';

type TellentClientAuthEndpoints = {
  tellentAuthTokenUrl: string;
  tellentUserUrl: string;
  tellentAuthUrl: string;
};

type AuthorizeOpts = {
  useOrgFallback: boolean;
  throwOnMissingAppGuid: boolean;
  isNativeMobileHr: boolean;
};

const defaultOpts: AuthorizeOpts = {
  useOrgFallback: true,
  throwOnMissingAppGuid: true,
  isNativeMobileHr: false,
};

export async function authorize(
  config: TellentClientAuthEndpoints,
  appKind: `${AppKind}`,
  partialOptions: Partial<AuthorizeOpts> = defaultOpts,
): Promise<TellentClientAuthInitData> {
  const { useOrgFallback, throwOnMissingAppGuid } = {
    ...defaultOpts,
    ...partialOptions,
  };

  const authResJson = partialOptions.isNativeMobileHr
    ? await fetchTokenOrThrowMobile()
    : await fetchTokenOrThrowWeb(config);
  const userResJson = await fetchUserOrThrow(config, authResJson!.accessToken);

  if (!authResJson || !userResJson) {
    throw new TellentClientBootstrapError();
  }

  const {
    user,
    accessToken: { kind: authKind },
  } = userResJson;
  const { accessToken } = authResJson;

  const orgGuid =
    authResJson.orgGuid ??
    readOrgGuidFromQueryParam() ??
    readOrgGuidFromLocalStorage(user.guid) ??
    (useOrgFallback ? readOrgGuidFallback(user.members) : null);

  if (!orgGuid) {
    throw new TellentClientOrgGuidUnavailableError({
      accessToken,
      user,
      appKind,
    });
  }

  user.currentOrgGuid = orgGuid;
  user.isAuthorizedWith = authKind;
  if (!validateOrgGuid(user, orgGuid)) {
    throw new TellentClientOrgGuidMismatchError({
      accessToken,
      user,
      appKind,
    });
  }

  const member = resolveMember(user, orgGuid);

  if (!member)
    throw new TellentClientMemberNotFoundInOrgError({
      accessToken,
      user,
      orgGuid,
      appKind,
    });

  const appGuid = resolveAppGuid(member, {
    accessToken,
    user,
    orgGuid,
    appKind,
    throwOnMissingAppGuid,
  });

  // TODO: What if LS is full? Fallback to other persistance mechanism?
  writeOrgGuidToLocalStorage(user.guid, orgGuid);

  return {
    error: false,
    member,
    user,
    accessToken,
    orgGuid,
    appGuid,
    appKind,
  };
}

type AuthErrorResult = ReturnType<typeof handleAuthError> & { appKind: `${AppKind}` };

export function authorizeErrorHandler(
  error: Error,
  config: TellentClientAuthEndpoints,
  appKind: `${AppKind}`,
): AuthErrorResult | undefined {
  if (error instanceof TellentClientAuthError) {
    const result: ReturnType<typeof handleAuthError> & { appKind: `${AppKind}` } = {
      ...(handleAuthError(error, config) || {}),
      appKind,
    };

    if (result.continueOnError) {
      return result;
    }
  }
}

export function handleAuthError(
  error: TellentClientAuthError,
  config: TellentClientAuthEndpoints,
): Partial<TellentClientAuthInitData> | null | void {
  switch (error.message) {
    case TellentClientAuthErrorCode.BootstrapError:
      throw error;

    case TellentClientAuthErrorCode.TokenUnavailable:
    case TellentClientAuthErrorCode.TokenInvalid:
      redirectToAuth(config);
      return null;

    case TellentClientAuthErrorCode.UserUnavailable:
      /**
       * TODO:
       * redirect to sign out?
       */
      throw error;

    case TellentClientAuthErrorCode.AppPending:
      redirectToSwitchForPendingApp(error.payload, config);
      return null;

    case TellentClientAuthErrorCode.MemberNotFoundInOrg:
    case TellentClientAuthErrorCode.MemberSeatNotFoundInOrg:
    case TellentClientAuthErrorCode.OrgGuidUnavailable:
    case TellentClientAuthErrorCode.OrgGuidMismatch:
      return {
        ...error.payload,
        errorCode: error.message,
      };

    default:
      return authErrorHandlerExhaustiveGuard(error.message);
  }
}

async function fetchTokenOrThrowMobile(): Promise<{
  accessToken: string;
  orgGuid: string | null;
} | null> {
  const user = await fetchTokenFromNative();
  if (!user) {
    throw new TellentClientTokenUnavailableError();
  }
  return user;
}

async function fetchTokenOrThrowWeb(
  config: TellentClientAuthEndpoints,
): Promise<{ accessToken: string; orgGuid: string | null } | null> {
  const authHeaders = new Headers();
  headers.forEach(([name, value]) => authHeaders.append(name, value));

  const authRes = await fetch(config.tellentAuthTokenUrl, {
    headers: authHeaders,
    mode: 'cors',
    credentials: 'include',
  });

  if (authRes.status === 401) throw new TellentClientTokenUnavailableError();

  const user = await authRes.json();
  return user;
}

async function fetchUserOrThrow(
  config: TellentClientAuthEndpoints,
  token: string,
): Promise<TellentUserResponse | null> {
  const userHeaders = new Headers();
  headers.forEach(([name, value]) => userHeaders.append(name, value));
  userHeaders.append('Authorization', `Bearer ${token}`);

  const userRes = await fetch(config.tellentUserUrl, {
    headers: userHeaders,
    mode: 'cors',
  });

  if (userRes.status === 401) throw new TellentClientTokenInvalidError();
  if (userRes.status !== 200) throw new TellentClientUserUnavailableError();

  const user = await userRes.json();
  return user;
}

function readOrgGuidFromLocalStorage(userGuid: string) {
  return localStorage.getItem(`preferred_org_guid_${userGuid}`);
}

function readOrgGuidFallback(members: TellentMember[]) {
  return members[0].orgGuid;
}

export function writeOrgGuidToLocalStorage(userGuid: string | null, orgGuid: string) {
  localStorage.setItem(`preferred_org_guid_${userGuid}`, orgGuid);
}

function readOrgGuidFromQueryParam() {
  const search = new URL(window.location.href).search;
  const params = new URLSearchParams(search);
  return params.get(TELLENT_AUTH_ORG_GUID_PARAM);
}

function redirectToAuth(config: TellentClientAuthEndpoints) {
  if (isNativeMobileHr) {
    openAuthBrowserInNativeApp();
  } else {
    window.open(`${config.tellentAuthUrl}?redirect_to=${window.location.href}`, '_self');
  }
}

function redirectToSwitchForPendingApp(
  payload: Partial<TellentClientAuthInitData> | null,
  config: TellentClientAuthEndpoints,
) {
  const fallbackSeat = payload?.member?.seats.find(s => s.app.status === 'active');
  const params = new URLSearchParams({
    app_kind: fallbackSeat?.app.kind ?? 'admin',
    org_guid: payload?.orgGuid ?? '',
  });

  window.open(`${config.tellentAuthUrl}/switch?${params.toString()}`, '_self');
}

function resolveMember(user: TellentCurrentUser, orgGuid: string): TellentMember | undefined {
  return user.members.find(m => m.orgGuid === orgGuid);
}

function resolveAppGuid(
  member: TellentMember,
  payload: Partial<TellentClientAuthInitData> & {
    appKind: `${AppKind}`;
    throwOnMissingAppGuid: boolean;
  },
): string {
  const { throwOnMissingAppGuid, ...errorPayload } = payload;
  const seat = member.seats.find(s => s.app.kind === payload.appKind);
  const appGuid = <string>seat?.appGuid;

  // settings (legacy 'admin') app doesn't have appGuid, hence opt out from throw
  if (throwOnMissingAppGuid && !appGuid) {
    throw new TellentClientMemberSeatNotFoundInOrgError(errorPayload);
  }

  // The app is still being set up on the backend (committed as :pending,
  // activated asynchronously) — don't enter it; the error handler redirects
  // to another app in the same org.
  if (seat?.app.status === 'pending') {
    throw new TellentClientAppPendingError({ ...errorPayload, member });
  }

  return appGuid;
}

function validateOrgGuid(user: TellentCurrentUser, orgGuid: string): boolean {
  return user.members.some(m => m.orgGuid === orgGuid);
}
